/**
 * The one `mm:ss` display timecode in the codebase.
 *
 * Six implementations of this had drifted apart before they were folded in
 * here (#636 case 2): minute padding differed, and one rounded instead of
 * flooring. The rounding one is why this is a shared module rather than a
 * convention — a difference of one second in a label is invisible in review
 * and obvious to whoever is reading it next to a waveform.
 *
 * FLOOR, never round. The label names a position on a clock the audio is
 * playing against, so a cue starting at `5:05.6` is at `5:05`: rounding up
 * points at a moment that has not happened yet, and at a minute boundary it
 * prints a second that does not exist (`59.9` would show `01:00` for a cue
 * inside the first minute).
 *
 * NOT for subtitle files. VTT and SRT cue timings quantize to milliseconds and
 * live in `vtt-formatter.ts`; flooring to a whole second there would move cue
 * boundaries.
 */

/**
 * Whole seconds for display: floored, never negative, and never `NaN`.
 *
 * Exported because `mm:ss` is not the only shape callers render — the outreach
 * admin list splits the same duration into `{ minutes, seconds }` parts so
 *「N 分 N 秒」stays in the locale files. What has to be shared between them is
 * this rounding rule, not the string.
 *
 * A non-finite input collapses to 0 rather than propagating: every caller
 * takes its seconds from a media element's `currentTime` or from a stored
 * duration, both of which read `NaN` before metadata loads, and `NaN:NaN` on
 * screen is worse than `00:00`.
 */
export function floorDisplaySeconds(totalSeconds: number): number {
  return Number.isFinite(totalSeconds) && totalSeconds > 0 ? Math.floor(totalSeconds) : 0
}

export interface FormatTimecodeOptions {
  /**
   * Zero-pad the minutes below an hour: `05:05` (default) vs `5:05`.
   *
   * Only affects the sub-hour form. Past an hour the minutes are always padded
   * in both, because `1:5:05` is not a timecode anyone reads.
   */
  padMinutes?: boolean
  /**
   * Always `hh:mm:ss`, hours two digits even at zero: the desktop meetings
   * page's recording clock (`00:12:34`), which must not change width an hour in.
   */
  withHours?: boolean
}

/** Seconds → `mm:ss`, rolling into `h:mm:ss` past an hour. */
export function formatSecondsAsTimecode(
  totalSeconds: number,
  options: FormatTimecodeOptions = {}
): string {
  const safe = floorDisplaySeconds(totalSeconds)
  const hours = Math.floor(safe / 3600)
  const minutes = Math.floor((safe % 3600) / 60)
  const seconds = safe % 60
  const ss = String(seconds).padStart(2, '0')
  if (options.withHours) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${ss}`
  }
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${ss}`
  const mm = options.padMinutes === false ? String(minutes) : String(minutes).padStart(2, '0')
  return `${mm}:${ss}`
}
